import { useEffect, useState } from 'react';
import { foundation } from '../../ui';

const { color, fontFamily, fontWeight } = foundation;

/** The open-menu count: soft blue chip, info numeral, 18px. */
export const openNoticeCountStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxSizing: 'border-box',
  fontFamily: fontFamily.sans,
  fontWeight: fontWeight.bold,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1,
  borderRadius: 999,
  minWidth: 18,
  height: 18,
  padding: '0 6px',
  backgroundColor: color.semantic.infoSoft,
  color: color.semantic.info,
  fontSize: 11,
};

/**
 * Nav notice count. Labeled rows (open sidebar, open drawer, labeled pill
 * tab) keep the icon clear and park the number at the end of the row.
 * Icon-only targets (collapsed rail, resting pill tab) use a small disc
 * with a surface ring so it sits in the corner instead of covering the glyph.
 */
const MORPH_MS = 280;
const MORPH_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

export default function NavNoticeCount({
  count, placement = 'end', ring = color.neutral.surface, style, morph = false, reducedMotion = false, morphGap = 8,
  /** 'info' = today's blue look; 'coral' = the menu's solid coral count. */
  tone = 'info',
}) {
  const coral = tone === 'coral';
  const n = Number(count) || 0;
  const [held, setHeld] = useState(n);
  const [open, setOpen] = useState(n > 0);

  useEffect(() => {
    if (!morph || reducedMotion) return undefined;
    if (n > 0) {
      setHeld(n);
      // Arrival swells in like the exit collapses: paint collapsed first, open
      // two frames later so the max-width tween runs (neighbours slide aside).
      let raf2;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setOpen(true));
      });
      return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
    }
    setOpen(false);
    const t = setTimeout(() => setHeld(0), MORPH_MS);
    return () => clearTimeout(t);
  }, [morph, reducedMotion, n]);

  const shown = morph && !reducedMotion ? held : n;
  if (shown <= 0) return null;
  const label = shown > 99 ? '99+' : String(shown);
  const collapsing = morph && !reducedMotion && !open;
  if (placement === 'corner') {
    return (
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxSizing: 'border-box',
          fontFamily: fontFamily.sans,
          fontWeight: fontWeight.bold,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
          borderRadius: 999,
          position: 'absolute',
          top: 2,
          right: 2,
          minWidth: 14,
          height: 14,
          padding: '0 3px',
          backgroundColor: coral ? color.brand.stopStrong : color.semantic.infoFill,
          color: color.brand.onPrimary,
          fontSize: 9,
          boxShadow: `0 0 0 2px ${ring}`,
          ...style,
        }}
      >
        {label}
      </span>
    );
  }

  const chip = (
    <span
      aria-hidden="true"
      style={{
        ...openNoticeCountStyle,
        marginLeft: morph ? 0 : 'auto',
        ...(coral ? { backgroundColor: color.brand.stopStrong, color: color.brand.onPrimary } : {}),
        ...style,
      }}
    >
      {label}
    </span>
  );

  if (!morph || reducedMotion) return chip;

  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        overflow: 'hidden',
        flexShrink: 0,
        maxWidth: collapsing ? 0 : 40,
        opacity: collapsing ? 0 : 1,
        marginLeft: collapsing ? -morphGap : 0,
        transform: collapsing ? 'scale(0.82)' : 'none',
        transformOrigin: 'center',
        transition: `max-width ${MORPH_MS}ms ${MORPH_EASE}, opacity ${MORPH_MS}ms ${MORPH_EASE}, margin-left ${MORPH_MS}ms ${MORPH_EASE}, transform ${MORPH_MS}ms ${MORPH_EASE}`,
      }}
    >
      {chip}
    </span>
  );
}
